import Loader from 'core/components/common/Loader'
import { DarkThemeContext } from 'core/contexts/DarkThemeContext'
import { IApplication } from 'core/interfaces/Application'
import { PromptDocumentsSection } from 'lauria/components/prompts/PromptDocumentsSection'
import { PromptFormActionBar } from 'lauria/components/prompts/PromptFormActionBar'
import { PromptFormAdvancedSection } from 'lauria/components/prompts/PromptFormAdvancedSection'
import { PromptFormatSelector } from 'lauria/components/prompts/PromptFormatSelector'
import { PromptFormFields } from 'lauria/components/prompts/PromptFormFields'
import { PromptFormSide } from 'lauria/components/prompts/PromptFormSide'
import {
  PromptFormTab,
  PromptFormTabs,
} from 'lauria/components/prompts/PromptFormTabs'
import { useCreatePromptForm } from 'lauria/hooks/useCreatePromptForm'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface PromptFormProps {
  application: IApplication
}

export const PromptForm: React.FC<PromptFormProps> = ({ application }) => {
  const { t } = useTranslation()
  const { isDarkTheme } = useContext(DarkThemeContext)
  const [displayDocs, setDisplayDocs] = useState(false)
  const [activeTab, setActiveTab] = useState<PromptFormTab>('basic')
  const {
    state,
    setters,
    fields,
    promptRef,
    addTextToTextarea,
    errors,
    onFormSubmit,
    cancel,
    setValue,
    register,
    watch,
    externalMcps,
    selectedAgents,
    setSelectedAgents,
  } = useCreatePromptForm()
  const returnToForm = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setDisplayDocs(false)
  }

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onFormSubmit()
  }

  if (state.isLoading) {
    return (
      <Loader
        center
        className="h-screen flex items-center justify-center w-full"
      />
    )
  }

  return (
    <>
      <div className="h-full w-full flex flex-col md:text-[13px] prompt-form-container">
        <div className="prompt-form-wrapper">
          <form
            id="prompt-form"
            className={`prompt-form-fields-content ${displayDocs ? 'hidden' : 'flex'}`}
          >
            <PromptFormTabs activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'basic' && (
              <div className="prompt-form-fields-inner">
                <PromptFormFields
                  nomField={fields.nomField}
                  errors={errors}
                  temperatureField={fields.temperatureField}
                  setValue={setValue}
                  register={register}
                  modeleField={fields.modeleField}
                  webSearchField={fields.webSearchField}
                />
                {![2, 4].includes(application.service.id) && (
                  <div>
                    <div className="h-[30px] mt-[15px]">
                      {t('prompts.form.attachDocuments')}
                    </div>
                    <div className="prompt-documents-section">
                      <PromptDocumentsSection
                        documents={state.documents}
                        setDocuments={setters.setDocuments}
                        newFiles={state.newFiles}
                        setNewFiles={setters.setNewFiles}
                      />
                    </div>
                  </div>
                )}
                <div className="h-[30px] mt-[10px]">
                  {t('prompts.form.formatLabel')}
                </div>
                <PromptFormatSelector
                  value={fields.formatField.value}
                  onChange={fields.formatField.onChange}
                />
              </div>
            )}
            {activeTab === 'advanced' && (
              <PromptFormAdvancedSection
                application={application}
                selectedAgents={selectedAgents}
                setSelectedAgents={setSelectedAgents}
                externalMcps={externalMcps.items}
                onAddExternalMcp={externalMcps.addItem}
                onUpdateExternalMcp={externalMcps.updateItem}
                onDeleteExternalMcp={externalMcps.deleteItem}
              />
            )}
          </form>
          <PromptFormSide
            watchPromptForm={watch}
            addTextToTextarea={addTextToTextarea}
            promptRef={promptRef}
            promptField={fields.promptField}
            errors={errors}
            promptDocuments={state.documents}
            promptNewFiles={state.newFiles}
          />
        </div>
        <PromptFormActionBar
          displayDocs={displayDocs}
          isDarkTheme={isDarkTheme}
          onCancel={cancel}
          onSubmit={handleSubmit}
          onReturnToForm={returnToForm}
          onToggleDisplayDocs={() => setDisplayDocs(true)}
        />
      </div>
    </>
  )
}
